import React from 'react';
import HomeIcon from '../icons/HomeIcon';
import ProjectsIcon from '../icons/ProjectsIcon';
import DrawingsIcon from '../icons/DrawingsIcon';
import TasksIcon from '../icons/TasksIcon';
import ReviewsIcon from '../icons/ReviewsIcon';
import TransmittalsIcon from '../icons/TransmittalsIcon';
import ReportsIcon from '../icons/ReportsIcon';
import TeamIcon from '../icons/TeamIcon';
import PreferencesIcon from '../icons/PreferencesIcon';
import AdminIcon from '../icons/AdminIcon';
import PermitsIcon from '../icons/PermitsIcon';
import AuditIcon from '../icons/AuditIcon';
import { NavLink } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { useProjects } from '../../hooks/useProjects';

/**
 * Sidebar
 *
 * Collapsible left navigation panel.
 * Renders primary navigation links grouped by section.
 *
 * TODO (axial-rebrand):
 *  - Wire active project name into header area
 *  - Connect notification badge count from NotificationContext
 *  - Add role-gated rendering for admin section (via useAuth)
 *  - Add collapse animation with CSS transition
 *
 * Props:
 *   open     — boolean, whether sidebar is expanded
 *   onToggle — callback to collapse/expand
 */

const navItems = [
  { label: 'Home',           path: '/dashboard',       icon: HomeIcon },
  { label: 'Projects',       path: '/projects',        icon: ProjectsIcon },
  { label: 'Drawings',       path: '/drawings',        icon: DrawingsIcon },
  { label: 'Tasks',          path: '/tasks',           icon: TasksIcon },
  { label: 'Reviews',        path: '/reviews',         icon: ReviewsIcon },
  { label: 'Permits',        path: '/permits',          icon: PermitsIcon },
  { label: 'Site Audit',     path: '/audit',            icon: AuditIcon, underConstruction: true },
  { label: 'Transmittals',   path: '/transmittals',    icon: TransmittalsIcon },
  { label: 'Reports',        path: '/reports',          icon: ReportsIcon, underConstruction: true },
  { label: 'Teams',          path: '/teams',            icon: TeamIcon },
  { label: 'Settings',       path: '/settings',         icon: PreferencesIcon },
];

const adminItems = [
  { label: 'Admin',          path: '/admin',            icon: AdminIcon },
];

function Sidebar({ open = true, onToggle, onNavigate }) {
  const { user, userProfile, activeTenant } = useAuth();
  const { projects, selectedProjectId } = useProjects(user);

  const canViewAdmin = Boolean(userProfile?.is_admin);

  return (
    <aside
      className={`
        flex flex-col bg-[var(--axial-bg)] border-r border-gray-700 transition-all duration-200
        ${open ? 'w-60' : 'w-16'}
      `}
    >
      {/* Logo / brand */}
      <div className="flex items-center gap-3 px-4 py-4 border-b" style={{ borderColor: 'var(--axial-border)' }}>
        {open ? (
          <img src="/axial-logo-main.svg" alt="Axial Logo" className="h-10 w-auto max-w-[240px] transition-all duration-200" />
        ) : (
          <img src="/axial-logo-trimmed.svg" alt="Axial Logo" className="h-5 w-5 transition-all duration-200" />
        )}
        <button
          onClick={onToggle}
          className={open ? 'ml-auto' : 'mr-2'}
          style={{ color: 'var(--axial-text-secondary)' }}
          aria-label="Toggle sidebar"
        >
          {open ? '←' : '→'}
        </button>
      </div>

      {/* Primary nav */}
      <nav className="flex-1 overflow-y-auto py-3 space-y-0.5" style={{ background: 'var(--axial-bg)' }}>
        {open && (
          <div className="mx-4 mb-3 rounded-md border px-3 py-2" style={{ borderColor: 'var(--axial-border)', background: 'var(--axial-surface-elevated)' }}>
            <p className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--axial-text-muted)' }}>Company</p>
            <p className="mt-1 truncate text-xs" style={{ color: 'var(--axial-text-primary)' }}>{activeTenant?.name || 'No company selected'}</p>
            <p className="text-[11px]" style={{ color: 'var(--axial-text-secondary)' }}>{projects.length} accessible</p>
          </div>
        )}

        {navItems.map(item => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={onNavigate}
              aria-disabled={item.underConstruction || undefined}
              title={item.underConstruction ? `${item.label} (under construction)` : undefined}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2 text-sm rounded-none transition-colors
                 ${item.underConstruction
                   ? 'text-gray-600 hover:text-gray-500 hover:bg-gray-800/40'
                   : isActive
                     ? 'bg-blue-600/20 text-blue-400 border-r-2 border-blue-500'
                     : 'text-gray-400 hover:text-gray-100 hover:bg-gray-800'
                 }`
              }
            >
              <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                <Icon style={{ width: 20, height: 20 }} />
              </span>
              {open && <span className="truncate">{item.label}</span>}
            </NavLink>
          );
        })}

        {/* Admin section — role-gated in future */}
        {canViewAdmin && (
          <div className="mt-2 pt-2 border-t border-gray-800">
            {adminItems.map(item => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2 text-sm rounded-none transition-colors
                     ${isActive
                       ? 'bg-blue-600/20 text-blue-400 border-r-2 border-blue-500'
                       : 'text-gray-400 hover:text-gray-100 hover:bg-gray-800'
                     }`
                  }
                >
                  <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                    <Icon style={{ width: 20, height: 20 }} />
                  </span>
                  {open && <span className="truncate">{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
        )}
      </nav>

      {/* User footer area */}
      {open && (
        <div className="px-4 py-3 border-t text-xs truncate" style={{ borderColor: 'var(--axial-border)', color: 'var(--axial-text-secondary)' }}>
          {userProfile?.display_name || 'User profile area'}
        </div>
      )}
    </aside>
  );
}

export default Sidebar;
